import React, { useEffect, useState } from 'react';
import { CheckIcon } from 'lucide-react';
import { Reveal } from './Reveal';
import { SectionHeading } from './SectionHeading';
const OFFER_DURATION_SECONDS = 23 * 60 * 60 + 59 * 60 + 59;
function getTimeUnits(totalSeconds: number) {
  return {
    hours: Math.floor(totalSeconds / 3600),
    minutes: Math.floor(totalSeconds % 3600 / 60),
    seconds: totalSeconds % 60
  };
}
type PlanCardProps = {
  pro?: boolean;
};
function PlanCard({
  pro = false
}: PlanCardProps) {
  const planName = pro ? 'PRO + Casos' : 'LISTO';
  const price = pro ? '27.990' : '19.990';
  const items = pro ? ['6 módulos del sistema', '4 bonos estratégicos', 'Plan de acción 90 días', '25 Casos de Éxito documentados', 'Entrenadores reales por disciplina'] : ['6 módulos del sistema', '4 bonos estratégicos', 'Plan de acción 90 días'];
  return <article className={`premium-card relative flex h-full flex-col ${pro ? 'border-gold bg-[#141210] p-7 sm:p-8' : 'bg-ink p-7 sm:p-8'}`} data-id="element-103">
      {pro ? <span className="absolute -top-3 left-6 bg-gold px-3 py-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.12em] text-ink" data-id="element-104">
          Más elegido
        </span> : null}
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-sage" data-id="element-105">
        Megapack Entrenadores
      </p>
      <h3 className="mt-3 font-display text-5xl font-bold leading-none tracking-[-0.04em] text-ivory" data-id="element-106">
        {planName}
      </h3>
      <p className="mt-7 font-mono text-4xl font-semibold tracking-[-0.05em] text-gold" data-id="element-107">
        ${price}
      </p>
      <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.1em] text-sage" data-id="element-108">
        ARS · pago único · Mercado Pago
      </p>
      <ul className="my-8 space-y-3.5" data-id="element-109">
        {items.map((item, index) => <li key={item} className={`flex gap-3 text-sm leading-5 ${pro && index > 2 ? 'font-semibold text-gold' : 'text-ivory'}`} data-id="element-110">
            <CheckIcon className="mt-0.5 shrink-0 text-gold" size={16} strokeWidth={2.5} aria-hidden="true" data-id="element-111" />
            {item}
          </li>)}
      </ul>
      <a href="https://librofy-store.myshopify.com/cart/47910701957341:1" className={`mt-auto flex min-h-12 items-center justify-center px-5 text-center font-mono text-[10px] font-bold uppercase tracking-wider transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold ${pro ? 'gold-button bg-gold text-ink' : 'border border-gold text-gold hover:bg-gold hover:text-ink'}`} data-id="element-112">
        Quiero el {pro ? 'PRO' : 'LISTO'} — ${price}
      </a>
      {pro ? <p className="mt-4 text-center text-xs text-sage" data-id="element-113">
          Acceso inmediato · Mercado Pago
        </p> : null}
    </article>;
}
export function PricingSection() {
  const [secondsLeft, setSecondsLeft] = useState(OFFER_DURATION_SECONDS);
  useEffect(() => {
    let endsAt = Number(window.sessionStorage.getItem('megapack-pricing-ends-at'));
    if (!endsAt || endsAt <= Date.now()) {
      endsAt = Date.now() + OFFER_DURATION_SECONDS * 1000;
      window.sessionStorage.setItem('megapack-pricing-ends-at', String(endsAt));
    }
    const updateTime = () => {
      const next = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));
      if (next === 0) {
        endsAt = Date.now() + OFFER_DURATION_SECONDS * 1000;
        window.sessionStorage.setItem('megapack-pricing-ends-at', String(endsAt));
        setSecondsLeft(OFFER_DURATION_SECONDS);
        return;
      }
      setSecondsLeft(next);
    };
    updateTime();
    const intervalId = window.setInterval(updateTime, 1000);
    return () => window.clearInterval(intervalId);
  }, []);
  const time = getTimeUnits(secondsLeft);
  const timeBoxes = [[time.hours, 'Horas'], [time.minutes, 'Min'], [time.seconds, 'Seg']];
  return <section id="pricing" className="border-y border-[#d4af37]/18 bg-card px-5 py-20 sm:px-8 sm:py-28" aria-label="Elegí tu versión" data-id="element-114">
      <div className="mx-auto max-w-6xl" data-id="element-115">
        <Reveal data-id="element-116">
          <SectionHeading centered eyebrow="Elegí tu versión" title="Un sistema completo. Pago único." description="Sin cuotas, sin suscripciones. Acceso inmediato." data-id="element-117" />
        </Reveal>

        <div className="mx-auto mt-14 grid max-w-[860px] gap-6 md:grid-cols-2" data-id="element-118">
          <Reveal className="order-2 md:order-1" data-id="element-119">
            <PlanCard data-id="element-120" />
          </Reveal>
          <Reveal className="order-1 md:order-2" delay={0.06} data-id="element-121">
            <PlanCard pro data-id="element-122" />
          </Reveal>
        </div>

        <Reveal className="mx-auto mt-10 max-w-[640px]" data-id="element-123">
          <div className="border border-[#d4af37]/45 bg-[#d4af37]/[0.05] p-5 sm:flex sm:items-center sm:justify-between sm:gap-6" data-id="element-124">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-gold-dark" data-id="element-125">
              Este precio cierra en
            </p>
            <div className="mt-4 flex justify-center gap-2 sm:mt-0" role="timer" aria-live="polite" data-id="element-126">
              {timeBoxes.map(([value, label]) => <div key={String(label)} className="text-center" data-id="element-127">
                  <div className="min-w-[68px] border border-gold bg-card px-3 py-2 font-mono text-2xl font-semibold text-gold" data-id="element-128">
                    {String(value).padStart(2, '0')}
                  </div>
                  <p className="mt-1 font-mono text-[8px] uppercase tracking-[0.12em] text-sage" data-id="element-129">
                    {label}
                  </p>
                </div>)}
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}